/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { SourceType, VideoResolution } from 'client-code/channels-service';
import { GuideProgram, GuideProgramsGetRequest } from 'client-code/filtered-guide';

import { DeviceFeatures } from '../../../core/device';
import { Channel } from '../../../core/entities';
import { Locale, LocaleKeys } from '../../../core/locale';
import { TChannelV2 } from '../../../core/types';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';

export const mapChannelTypeIndexToChannelFilter = (
    channelTypeIndex: string,
): GuideProgramsGetRequest['channelFilter'] => {
    switch (channelTypeIndex) {
        case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES):
            return 'favorites';
        case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS):
            return 'subscribed';
        default:
            return undefined;
    }
};

export const mapCategoryTypeIndexToChannelFilter = (
    categoryIndex: string,
): GuideProgramsGetRequest['category'] | undefined => {
    switch (categoryIndex) {
        case Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_SPORTS).toLocaleLowerCase():
            return 'sports';
        case Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_KIDS).toLocaleLowerCase():
            return 'kids';
        case Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_NEWS).toLocaleLowerCase():
            return 'news';
        case Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_MOVIES).toLocaleLowerCase():
            return 'movies';
        default:
            return undefined;
    }
};

export const isChannelPartOfFilteredOutChannels = (channel: TChannelV2 | Channel) => {
    // TODO: Remove this check after completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
    const isJumpChannelCcbEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
    );
    const filterJump = !isJumpChannelCcbEnabled && DeviceFeatures.shouldFilterJumpChannels();
    const filterPpv = DeviceFeatures.shouldFilterPpvChannels();
    const filter4K = DeviceFeatures.shouldFilter4KChannels();

    return (
        (filterJump && channel.sourceType === SourceType.JumpChannel) ||
        (filterPpv && channel.isPayPerView) ||
        (filter4K && channel.videoResolution === VideoResolution.Uhd)
    );
};

export const filterExcludedPrograms = (
    programs: GuideProgram[],
    allPossibleChannels: Array<TChannelV2 | Channel>,
): GuideProgram[] => {
    const filterPpv = DeviceFeatures.shouldFilterPpvChannels();
    return programs.filter((program) => {
        // Filter out PPV programs based on program-level flag
        if (filterPpv && program.isPPV) return false;

        // Filter out programs from excluded channels (PPV, UHD, Jump channels)
        if (allPossibleChannels.length > 0) {
            const channel = allPossibleChannels.find((c) => c.channelId === program.channelId);
            if (channel && isChannelPartOfFilteredOutChannels(channel)) return false;
        }

        return true;
    });
};

export const getReasonWhyChannelIsFilteredOut = (channel: TChannelV2 | Channel): string => {
    // TODO: Remove this check after completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
    const isJumpChannelCcbEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
    );
    if (
        !isJumpChannelCcbEnabled &&
        DeviceFeatures.shouldFilterJumpChannels() &&
        channel.sourceType === SourceType.JumpChannel
    ) {
        return Locale.getString(LocaleKeys.JUMP_CHANNELS_CURRENTLY_UNSUPPORTED);
    }
    if (DeviceFeatures.shouldFilterPpvChannels() && channel.isPayPerView) {
        return Locale.getString(LocaleKeys.PPV_CHANNELS_CURRENTLY_UNSUPPORTED);
    }
    if (
        DeviceFeatures.shouldFilter4KChannels() &&
        channel.videoResolution === VideoResolution.Uhd
    ) {
        return Locale.getString(LocaleKeys.FOUR_K_CHANNELS_CURRENTLY_UNSUPPORTED);
    }

    return '';
};

// Utility functions copied from ContentPreviewComponent.tsx
export const getPreviewDescriptionAttributes = (
    videoResolution: string = '',
    hasCc: boolean = false,
    hasSap: boolean = false,
    is3D: boolean = false,
) => {
    const showAttributes: Array<string> = [];
    if (hasCc) {
        showAttributes.push('CC');
    }
    if (hasSap) {
        showAttributes.push('SAP');
    }
    if (is3D) {
        showAttributes.push('3D');
    }
    if (!is3D && videoResolution) {
        showAttributes.push(videoResolution.toLocaleUpperCase());
    }

    return showAttributes;
};

export const calculateMaskSizeClass = (detailsString: string): string => {
    if (detailsString.length > 0 && detailsString.length < 4) {
        return 'mask-size-x-small';
    }
    if (detailsString.length >= 4 && detailsString.length < 7) {
        return 'mask-size-small';
    }
    if (detailsString.length >= 7 && detailsString.length < 12) {
        return 'mask-size-medium';
    }
    if (detailsString.length >= 12) {
        return 'mask-size-large';
    }
    return '';
};
